import React, { useState, useRef } from 'react';
import {
  FiChevronDown,
  FiMenu,
  FiSettings,
  FiUser,
  FiHome,
  FiBarChart2,
  FiTarget,
  FiDollarSign,
  FiShield,
  FiTrendingUp,
  FiMap,
  FiUsers,
  FiAlertTriangle,
  FiPieChart,
  FiBriefcase,
  FiSearch,
  FiZap,
} from 'react-icons/fi';


const SidepanelTest = ({ children, insightData, onTabChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandSections, setExpandSections] = useState({});
  const sidepanelRef = useRef(null);

  // Function to handle tab changes and notify parent
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Close the panel when a tab is selected
    setIsOpen(false);
    // Notify parent component about tab change
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  // Section toggling
  const toggleSection = (key) => {
    setExpandSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const Section = ({ title, iconComponent, children, collapsible, expanded, onToggle }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-xl mb-6 p-6 transition-all duration-200">
      <div 
        className={`font-semibold text-sm mb-2 flex items-center gap-2 ${
          collapsible ? 'cursor-pointer' : ''
        }`}
        onClick={collapsible ? onToggle : undefined}
      >
        {iconComponent && React.createElement(iconComponent, { className: "w-4 h-4 text-gray-700" })} 
        {title}
        {collapsible && (
          <FiChevronDown
            className={`ml-auto w-5 h-5 text-gray-500 transition-transform duration-200 ${
              expanded ? 'rotate-0' : '-rotate-90'
            }`}
          />
        )}
      </div>
      {(!collapsible || expanded) && <div className="mt-4">{children}</div>}
    </div>
  );

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiHome, color: '#3b82f6' },
    { id: 'stakeholders', label: 'Stakeholders', icon: FiUsers, color: '#8b5cf6' },
    // { id: 'value', label: 'Value Intelligence', icon: FiDollarSign, color: '#10b981' },
    { id: 'risks', label: 'Risk Assessment', icon: FiShield, color: '#f59e0b' },
    { id: 'actions', label: 'Actions', icon: FiTarget, color: '#ef4444' },
    { id: 'timeline', label: 'Timeline', icon: FiTrendingUp, color: '#06b6d4' },
    { id: 'competitive', label: 'Competitive', icon: FiBarChart2, color: '#84cc16' },
    { id: 'expansion', label: 'Expansion', icon: FiMap, color: '#f97316' },
    { id: 'research', label: 'Research', icon: FiSearch, color: '#6366f1' },
    { id: 'signals', label: 'Signals', icon: FiZap, color: '#ef4444' },
  ];

  // Manual toggle function
  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative h-screen">
      {/* Side Panel with Tabs */}
      <div
        ref={sidepanelRef}
        className={`fixed left-0 top-0 w-52 h-screen bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 border-r border-gray-200 overflow-hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Panel Header */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-white text-gray-800 p-2 rounded-lg">
              <FiBriefcase className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-bold m-0">
              {insightData?.executive_intelligence_summary?.account_status_vector?.company_name || 'Company Insights'}
            </h2>
          </div>
          <button
            onClick={togglePanel}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 border-none rounded-full w-8 h-8 flex items-center justify-center text-white cursor-pointer transition-all duration-200"
          >
            ✕
          </button>
        </div>
        <div className="text-xs opacity-90 px-5 pb-5">
          Powered by AURA
        </div>

        {/* Tabs Navigation */}
        <div className="pb-5">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-all duration-200 ${
                activeTab === tab.id 
                  ? 'bg-gray-100 border-r-2 border-gray-800 text-gray-900' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {React.createElement(tab.icon, { 
                className: `w-5 h-5 ${
                  activeTab === tab.id ? 'text-gray-800' : 'text-gray-500'
                }`
              })}
              <span className={`font-medium text-sm ${
                activeTab === tab.id ? 'font-semibold' : 'font-medium'
              }`}>
                {tab.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ease-in-out h-screen overflow-auto ${
        isOpen ? 'ml-52' : 'ml-0'
      }`}>
        {children}
      </div>

      {/* Floating Toggle Button (when panel is closed) */}
      {!isOpen && (
        <button
          onClick={togglePanel}
          className="fixed left-3 top-4 bg-gray-800 hover:bg-gray-900 text-white border-none rounded-full w-12 h-12 flex items-center justify-center cursor-pointer shadow-lg hover:shadow-xl z-40 transition-all duration-200 hover:scale-110"
        >
          <FiMenu className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default SidepanelTest;
